import {CommonModule, DatePipe} from '@angular/common';
import {Component, EventEmitter, Inject, Input, Output} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {EmMarkdownPipe, FeatureFlagService, TranslationService, validators} from '@emma-labs/shared/angular';
import {
  ENVIRONMENT,
  EnvironmentBroker,
  FeatureFlags,
  HealthAnswers,
  HealthAnswersRevisionType,
  removeNullProperties,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {CheckboxModule} from 'primeng/checkbox';
import {DividerModule} from 'primeng/divider';
import {FileRemoveEvent, FileSelectEvent, FileUploadModule} from 'primeng/fileupload';
import {InputTextareaModule} from 'primeng/inputtextarea';
import {debounceTime, first, tap} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {
  ExtraQuestionnaire,
  ExtraQuestionnaireInfo,
} from '../../../entities/archived-proposal-object/archived-proposal-page-object';
import {RevisionAnswers} from '../../../entities/archived-proposal-object/revision-answers';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {BaseSectionComponent} from '../../base-section/base-section.component';
import {QuestionComponent} from '../../question/question.component';

interface QuestionnaireForm {
  [key: string]: AbstractControl<unknown, unknown>;

  custom: FormControl<string | null | undefined>;
  documents: FormControl<File[] | null | undefined>;
}

@UntilDestroy()
@Component({
  selector: 'em-extra-questionnaires-section',
  templateUrl: './extra-questionnaires-section.component.html',
  imports: [
    CommonModule,
    CheckboxModule,
    ReactiveFormsModule,
    CardModule,
    QuestionComponent,
    DividerModule,
    DatePipe,
    ButtonModule,
    FileUploadModule,
    InputTextareaModule,
    EmMarkdownPipe,
    FormsModule,
  ],
})
export class ExtraQuestionnairesSectionComponent extends BaseSectionComponent<
  FormArray<FormGroup<QuestionnaireForm>>,
  ExtraQuestionnaireInfo,
  RevisionAnswers[]
> {
  extraQuestionnaires: ExtraQuestionnaire[] = [];
  isLoading = false;
  allExtraQuestionnairesCompleted = false;
  canModifyIsIncludedInContract = false;
  canAnswerQuestions = false;
  isFFEnabled = false;

  readonly healthAnswersRevisionType = HealthAnswersRevisionType;
  readonly customControlName = 'custom';
  readonly documentsControlName = 'documents';

  @Input({required: true})
  override set props(extraQuestionnaireInfo: ExtraQuestionnaireInfo | undefined) {
    if (!extraQuestionnaireInfo?.extraQuestionnaires) {
      this.extraQuestionnaires = [];
      return;
    }

    this.canModifyIsIncludedInContract = extraQuestionnaireInfo.canModifyIsIncludedInContract;
    this.canAnswerQuestions = extraQuestionnaireInfo.canAnswerQuestions;

    const sortedExtraQuestionnaires = this.sortExtraQuestionnaires(
      extraQuestionnaireInfo?.extraQuestionnaires,
    );

    if (
      this.extraQuestionnaires.length !== sortedExtraQuestionnaires.length ||
      this.extraQuestionnaires.some((eq, i) => eq.completedAt !== sortedExtraQuestionnaires[i].completedAt)
    ) {
      this.buildForm(sortedExtraQuestionnaires);
      this.extraQuestionnaires = sortedExtraQuestionnaires;
    }

    this.allExtraQuestionnairesCompleted = this.extraQuestionnaires.every((eq) => eq.completedAt);
    this.isLoading = false;
  }

  @Output() downloadDocument = new EventEmitter<{
    id: string;
    documentName: string;
    type: 'haRevision' | 'extraRequirements';
  }>();
  @Output() uploadDocuments = new EventEmitter<{
    healthAnswersRevisionId: string;
    documents: File[];
    callback: (status: boolean) => void;
  }>();
  @Output() deleteDocument = new EventEmitter<{
    healthAnswersRevisionId: string;
    documentName: string;
    callback: (status: boolean) => void;
  }>();
  @Output() completeQuestionnaires = new EventEmitter<{
    output: string[];
    callback?: (status: boolean) => void;
  }>();
  @Output() updateIsIncludedInContract = new EventEmitter<{
    isIncludedInContract: boolean;
    healthAnswersRevisionId: string;
  }>();

  constructor(
    readonly translate: TranslationService,
    private readonly fb: FormBuilder,
    private readonly featureFlagService: FeatureFlagService,
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
  ) {
    super(environment);
    this.form = this.createForm();

    this.featureFlagService
      .isEnabled(FeatureFlags.IS_MANUALLY_ADDED_TO_CONTRACT_ENABLED)
      .pipe(first())
      .subscribe((isEnabled) => {
        this.isFFEnabled = isEnabled;
      });
  }

  override formToOutput(): RevisionAnswers[] {
    const output: RevisionAnswers[] = [];
    const mergedAnswers = new Map<string, HealthAnswers>();

    this.form.controls.forEach((fg, i) => {
      const extraQuestionnaire = this.extraQuestionnaires[i];
      if (extraQuestionnaire.completedAt) {
        return;
      }

      const revisionId = extraQuestionnaire.revisionId;

      if (!revisionId) {
        return; // In case of no revisionId there is no output possible
      }

      if (extraQuestionnaire.type === HealthAnswersRevisionType.CUSTOM) {
        if (fg.controls.custom.value) {
          output.push({
            id: revisionId,
            answers: removeNullProperties(fg.controls.custom.value),
          });
        }
      } else {
        mergedAnswers.set(revisionId, {
          ...removeNullProperties(fg.getRawValue()),
          ...mergedAnswers.get(revisionId),
        });
      }
    });

    output.push(...[...mergedAnswers.entries()].map(([k, v]) => ({id: k, answers: v})));

    return output;
  }

  getFormControl(index: number, controlName: string): FormControl {
    return this.form.controls[index].get(controlName) as FormControl;
  }

  completeExtraQuestionnaires(): void {
    this.isLoading = true;

    const revisionsIds = [
      ...new Set(
        this.extraQuestionnaires.filter((eq) => eq.revisionId && !eq.completedAt).map((eq) => eq.revisionId),
      ),
    ];
    if (!revisionsIds || revisionsIds.length === 0) {
      throw new Error('No revisionId found');
    }

    this.completeQuestionnaires.emit({
      output: revisionsIds as string[],
      callback: () => (this.isLoading = false),
    });
  }

  onDownloadDocument(documentName: string, index: number) {
    const eq = this.extraQuestionnaires[index];
    if (!eq.revisionId) {
      throw new Error('No revisionId found');
    }

    this.downloadDocument.emit({id: eq.revisionId, documentName, type: 'haRevision'});
  }

  onSelectDocuments($event: FileSelectEvent, index: number) {
    const selectedFiles: File[] = Array.from($event.files); // files is of type File[] but it is actually of type FileList if you log it

    const eq = this.extraQuestionnaires[index];
    if (!eq.revisionId) {
      throw new Error('No revisionId found');
    }

    this.uploadDocuments.emit({
      healthAnswersRevisionId: eq.revisionId,
      documents: selectedFiles,
      callback: (status) => {
        if (status) {
          this.getFormControl(index, this.documentsControlName).setValue($event.currentFiles, {
            emitEvent: false,
          });
        } else {
          this.getFormControl(index, this.documentsControlName).reset(null);
          this.buildForm(this.extraQuestionnaires);
        }
      },
    });
  }

  onRemoveDocument($event: FileRemoveEvent, index: number) {
    const eq = this.extraQuestionnaires[index];
    if (!eq.revisionId) {
      throw new Error('No revisionId found');
    }
    this.deleteDocument.emit({
      healthAnswersRevisionId: eq.revisionId,
      documentName: $event.file.name,
      callback: (status) => {
        if (status) {
          const documentsControl = this.form.controls[index].controls.documents;
          const updatedFiles = documentsControl.value?.filter((file: File) => file.name !== $event.file.name);
          documentsControl.setValue(updatedFiles || null, {emitEvent: false});
        }
      },
    });
  }

  onUpdateIsIncludedInContract($event, revisionId) {
    this.updateIsIncludedInContract.emit({
      isIncludedInContract: $event.checked,
      healthAnswersRevisionId: revisionId,
    });
  }

  private buildForm(extraQuestionnaires: ExtraQuestionnaire[]) {
    this.form.controls = [];

    extraQuestionnaires.forEach((eq) => {
      const formGroup = this.fb.group<QuestionnaireForm>({} as QuestionnaireForm);

      if (eq.type === HealthAnswersRevisionType.CUSTOM) {
        formGroup.addControl('custom', this.fb.control(null, [validators.notEmpty]));
        formGroup.addControl('documents', this.fb.control(null));
      }

      if (eq.type === HealthAnswersRevisionType.PRESET && eq.presetQuestions) {
        eq.presetQuestions.forEach((q) =>
          formGroup.addControl(
            q.answer.propertyName,
            this.fb.control(q.answer.value, [validators.notEmpty]),
            {emitEvent: false},
          ),
        );
      } else {
        formGroup.controls.custom.setValue(eq.customQuestion?.answer, {emitEvent: false});

        if (eq.customQuestion?.documentNames && eq.customQuestion?.documentNames.length > 0) {
          const files = eq.customQuestion.documentNames.map((name) => new File([], name));
          formGroup.controls.documents.setValue(files, {emitEvent: false});
        }
      }

      if (eq.completedAt || this.isReadonly || (!this.canAnswerQuestions && this.isFFEnabled)) {
        formGroup.disable({emitEvent: false});
      }

      this.form.push(formGroup, {emitEvent: false});
    });
  }

  private createForm(): FormArray<FormGroup<QuestionnaireForm>> {
    const form = this.fb.array<FormGroup<QuestionnaireForm>>([]);

    form.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(() => (this.isPendingUpdate = true)),
        debounceTime(DEBOUNCE_TIME),
        untilDestroyed(this),
      )
      .subscribe((_) => {
        this.propsChange.emit({
          output: this.formToOutput(),
          callback: (_) => (this.isPendingUpdate = false),
        });
      });

    return form;
  }

  private sortExtraQuestionnaires(extraQuestionnaires: ExtraQuestionnaire[]): ExtraQuestionnaire[] {
    return extraQuestionnaires.sort((a, b) => {
      // If both have completedAt, sort by completedAt
      if (a.completedAt && b.completedAt) {
        return new Date(a.completedAt) < new Date(b.completedAt) ? -1 : 1;
      }
      // If only one is completed, the completed one comes first
      if (a.completedAt || b.completedAt) {
        return a.completedAt ? -1 : 1;
      }
      // If completedAt is equal or both are incomplete, sort by type
      if (a.type !== b.type) {
        return a.type < b.type ? -1 : 1;
      }
      // If types are equal, sort by title
      return a.title.localeCompare(b.title);
    });
  }
}
